import { useCallback, useRef } from "react";

/**
 * `useDebouncedCallback` is a custom hook that returns a debounced version of the callback function.
 * The debounced function will delay invoking the callback until after `delay` milliseconds have elapsed
 * since the last time the debounced function was invoked.
 *
 * @param {Function} callback - The callback function to debounce.
 * @param {number} [delay=300] - The amount of delay in milliseconds.
 *
 * @returns {Function} The debounced callback function.
 */
export const useDebouncedCallback = (callback, delay = 300) => {
    // Create a ref to store the callback
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    // Create a ref to store the timer id
    const timerRef = useRef();

    // Return a function that, when called, clears any existing timers and sets a new one
    return useCallback(
        (...args) => {
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                callbackRef.current(...args);
            }, delay);
        },
        [delay]
    ); // Only recreate if delay changes
};

export default useDebouncedCallback;
